import { defaultsDeep } from 'lodash/fp';
import isEmpty from 'lodash/isEmpty';
import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Order } from '@nexelem/api';

import { TypedFiltersProps } from '../../types';
import { parseObject } from './parser';

type Callback<T> = (oldValue: T) => T;

export type SearchParamsProps<Filters = unknown> = {
  readonly data: Filters;
  readonly ordering?: Order;
};

type QueryParamsResult<T> = [filters: TypedFiltersProps, callback: (prevState: Callback<T>) => void];
export const useQueryParams = <T extends TypedFiltersProps<any>>(
  initFilters: T,
  consistencyInitChecker?: (initFilters: T) => T,
): QueryParamsResult<T> => {
  const location = useLocation();

  const parseSearchString = useCallback((search: string, initFilters: T, checker?: (initFilters: T) => T) => {
    const parse = qs.parse(search, { ignoreQueryPrefix: true });
    const filters = defaultsDeep(initFilters, parse);
    const object = parseObject(filters) as T;

    return typeof checker === 'function' ? checker(object) : object;
  }, []);

  // useSearchParams - we can't use this hook because:
  // 1. https://github.com/remix-run/react-router/discussions/9851
  // 2. trigger re-render whole page if query params change

  const [searchParams, setSearchParams] = useState<T>(() =>
    parseSearchString(location.search, initFilters, consistencyInitChecker),
  );

  useEffect(() => {
    const listener = () => {
      const searchData = parseSearchString(window.location.search, initFilters);
      setSearchParams(searchData);
    };
    window.addEventListener('popstate', listener);
    return () => {
      window.removeEventListener('popstate', listener);
    };
    // we don't want watch to init filters changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parseSearchString]);

  const getNewFilterValues = useCallback((filters: T) => {
    return {
      ...filters,
      data: Object.keys(filters.data || {}).reduce((acc, key) => {
        return {
          ...acc,
          [key]: { value: filters.data?.[key]?.value },
        };
      }, {}),
    };
  }, []);

  const onChangeFilters = useCallback(
    (getNewFilters: Callback<T>) => {
      setSearchParams((prevStateSearch: T) => {
        const previousOrInitFilters = isEmpty(prevStateSearch) ? initFilters : prevStateSearch;
        const newFilters = getNewFilters(previousOrInitFilters);
        const newFilterValues = getNewFilterValues(newFilters);
        const url = ['?', qs.stringify(newFilterValues)].join('');
        window.history.pushState({}, '', [location.pathname, url].join(''));
        location.search = url;
        return newFilterValues;
      });
    },
    [getNewFilterValues, initFilters, location],
  );

  return [searchParams, onChangeFilters];
};
